/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import Title from "../../ui/Title";
import Form from "../../ui/form/Form";
import TextField from "../../ui/form/TextField";
import Button from "../../ui/Button";
import FormGroup from "../../ui/form/FormGroup";
import FormError from "../../ui/form/FormError";
import Icon from "../../ui/Icon";
import Text from "../../ui/Text";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/Tooltip";
import { useTranslate } from "../../../hooks/useTranslate";
import { isActivationKey } from "../../../hooks/a11y/activationKeys";
import { usePromotionCode } from "../../../hooks/checkout/usePromotionCode";
import Flex from "../../ui/Flex";
import LoadingIndicator from "../../LoadingIndicator";
import Stack from "../../ui/Stack";
import FormatPrice from "../../formatters/FormatPrice";
import { DISCOUNT_TYPE } from "../../../constants/constants";

const PromoCodeSection = styled.div`
  display: grid;
  gap: 16px;
  width: 100%;
  padding: 16px;
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 1rem;
  box-shadow: 2px 2px 15px rgba(${({ theme }) => theme.colors.text.rgb}, 0.1);
  ${(props) => props.$disabled && "opacity: 0.5;"}
  ${(props) => props.$disabled && "pointer-events: none;"}

  ${({ theme }) => theme.containerMedia.sm} {
    padding: 0;
    box-shadow: none;
    border: none;
  }
`;

const PromoCodeSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${({ $isOpen, theme }) => !$isOpen && theme.containerMedia.sm} {
    flex-direction: column;
    align-items: unset;
    gap: 1rem;
  }
`;

const AppliedCode = styled(Flex)`
  padding: 0.275rem 0.5rem;
  border-radius: 0.5rem;
  background: rgba(${({ theme }) => theme.colors.success.rgb}, 0.10);
`;

const PromoCodeForm = styled((props) => <Form {...props} />)`
  display: grid;
  gap: 0.5rem;
`;

const StyledTooltipContent = styled((props) => <TooltipContent {...props} />)`
  max-width: 250px;
`;

const AppliedCodeDeleteIcon = styled((props) => <Icon.Cross {...props} />)`
  cursor: pointer;
  border-radius: 0.25rem;
  outline: none;

  &:focus-visible {
    outline: 2px solid rgba(${({ theme }) => theme.colors.error.rgba});
    outline-offset: 2px;
  }
`;

const PromoCodeInput = ({ disabled }) => {
  const { _t } = useTranslate();
  const {
    handleSubmit,
    setError,
    reset: resetForm,
    control,
    clearErrors,
    formState: { errors },
    setValue,
  } = useForm();
  const [isOpen, setIsOpen] = useState(false);

  const {
    isApplying,
    deletingId,
    error,
    appliedPromotionCodes,
    appliedCount,
    hasExistingPromotion,
    promotionCode,
    applyCode,
    deleteCode,
    reset: resetPromotion,
  } = usePromotionCode({ disabled });

  // Auto-open form when promotion code is pre-filled (e.g., from URL)
  useEffect(() => {
    if (promotionCode && !isOpen) {
      setIsOpen(true);
      setValue("code", promotionCode);
    }
  }, [promotionCode, isOpen, setValue]);

  const handleApply = useCallback(
    (data) => {
      if (!data.code) {
        setError("code", { type: "manual", message: "error_value_is_empty" });
        return;
      }
      applyCode(data.code, {
        // Stay open and clear the field so another code can be applied immediately
        onSuccess: () => resetForm({ code: "" }),
      });
    },
    [applyCode, setError, resetForm]
  );

  const handleDelete = useCallback(
    (instanceId) => {
      deleteCode(instanceId);
    },
    [deleteCode]
  );

  const handleDeleteKeyDown = useCallback(
    (event, instanceId) => {
      if (isActivationKey(event)) {
        event.preventDefault();
        handleDelete(instanceId);
      }
    },
    [handleDelete]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    resetForm({ code: "" });
    clearErrors();
    resetPromotion();
  }, [resetForm, clearErrors, resetPromotion]);

  const appliedCountLabel =
    appliedCount === 1
      ? _t("%count% code applied", { count: appliedCount })
      : _t("%count% codes applied", { count: appliedCount });

  return (
    <PromoCodeSection $disabled={disabled}>
      <PromoCodeSectionHeader $isOpen={isOpen}>
        <Stack $gap="0">
          <Title size="h3">Promocode</Title>
          <Flex $gap="0.25rem" $align="center">
            <Text>{hasExistingPromotion ? appliedCountLabel : "Have promocode? Add it here"}</Text>
            <Tooltip placement="bottom-left">
              <TooltipTrigger>
                <Icon.InfoSquareSolidIcon color="secondary" size={Icon.SIZE.SM} />
              </TooltipTrigger>
              <StyledTooltipContent>
                <Text color="dark">promotion_gift_card_appliction_order_warning</Text>
              </StyledTooltipContent>
            </Tooltip>
          </Flex>
        </Stack>

        {!isOpen ? (
          <Button
            variant="outline"
            icon={<Icon.Plus color="dark" icon={Icon.SIZE.MD} />}
            title="Add code"
            onClick={() => setIsOpen(true)}
          />
        ) : (
          <Button variant="transparent" onClick={handleClose}>
            Close
          </Button>
        )}
      </PromoCodeSectionHeader>

      {hasExistingPromotion && (
        <Flex $wrap="wrap" $gap="0.5rem">
          {appliedPromotionCodes.map((instance) => {
            const promotion = instance.promotion || {};
            const hasDiscount = promotion.discount != null;
            const discountValue =
              promotion.discount_type === DISCOUNT_TYPE.PERCENTAGE
                ? `${promotion.discount}%`
                : <FormatPrice value={promotion.discount} currency={promotion.currency} />;

            return (
              <AppliedCode key={instance.id} $gap="0.5rem" $align="center">
                <Text color="dark">
                  {instance.code}
                  {hasDiscount && <> | {discountValue}</>}
                </Text>
                {deletingId === instance.id ? (
                  <LoadingIndicator size="xs" />
                ) : (
                  <AppliedCodeDeleteIcon
                    color="danger"
                    role="button"
                    tabIndex={0}
                    aria-label={`${_t("Remove promocode")}: ${instance.code}`}
                    onClick={() => handleDelete(instance.id)}
                    onKeyDown={(event) => handleDeleteKeyDown(event, instance.id)}
                  />
                )}
              </AppliedCode>
            );
          })}
        </Flex>
      )}

      {isOpen && (
        <PromoCodeForm>
          <FormGroup>
            <TextField control={control} name="code" disabled={disabled} />
          </FormGroup>
          <Button
            variant="primary"
            title="Apply promocode"
            onClick={handleSubmit(handleApply)}
            loading={isApplying}
          />
        </PromoCodeForm>
      )}

      {errors.code && <FormError name="code">{errors.code.message}</FormError>}
      {error && <FormError>{error}</FormError>}
    </PromoCodeSection>
  );
};

export default PromoCodeInput;
